import Paragraph from 'components/Paragraph';
import { Document } from 'prismic-javascript/src/documents';
import { Elements, RichText } from 'prismic-reactjs';
import type { HTMLSerializer, RichTextBlock } from 'prismic-reactjs';
import React, { ReactNode } from 'react';

const CSS = require('./DefaultPrismic.module.scss');

export type BannerSliceValue = {
  title: { text: string }[];
  description: RichTextBlock[];
  background: string;
};

export type ArticleSliceValue = {
  title: string;
  content: RichTextBlock[];
};

export type BannerSlice = {
  slice_type: string;
  slice_label: string;
  value: BannerSliceValue[];
};

export type ArticleSlice = {
  slice_type: string;
  slice_label: string;
  value: ArticleSliceValue[];
};

type InterlinkingData = {
  title: string;
  links?: Array<Record<string, unknown>>;
};

type InterlinkingDocument = Omit<Document, 'data'> & { data: InterlinkingData };

type InterlinkingBlockColumn = { title: string; url: string }[];
export type InterlinkingBlockProps = {
  title: string;
  linkColumns: InterlinkingBlockColumn[];
};

export type Banner = {
  title: string;
  description: ReactNode[];
  image: string;
};

export type Article = {
  title: string;
  content: ReactNode[];
};

const propsWithUniqueKey = (
  props: Record<string, unknown> | undefined,
  key: React.Key,
): Record<string, unknown> => Object.assign(props || {}, { key });

// adapted from the prismic docs found at https://prismic.io/docs/reactjs/beyond-the-api/html-serializer
const htmlSerializer: HTMLSerializer<React.ReactNode> = (
  type,
  element,
  _content,
  children,
  key,
) => {
  let props: Record<string, unknown> = {};
  switch (type) {
    case Elements.paragraph:
      props = { className: CSS.Paragraph };
      return (
        <Paragraph {...propsWithUniqueKey(props, key)}>{children}</Paragraph>
      );
    case Elements.hyperlink:
      const targetAttr = element?.data?.target
        ? { target: element.data.target as string }
        : {};
      const relAttr = element?.data?.target ? { rel: 'noopener' } : {};
      props = Object.assign(
        {
          className: CSS.Anchor,
          href: element?.data?.url as string,
        },
        targetAttr,
        relAttr,
      );

      return <a {...propsWithUniqueKey(props, key)}>{children}</a>;
  }
  return null;
};

class PrismicResponseParser {
  static parseBannerSlice = (slice: BannerSlice): Banner => {
    const sliceValue = slice.value[0];
    const title = sliceValue?.title?.[0]?.text ?? '';

    const description = [
      <RichText
        htmlSerializer={htmlSerializer}
        key={`${title}`}
        render={sliceValue.description}
      />,
    ];

    const image = sliceValue.background;

    return {
      title,
      description,
      image,
    };
  };

  static parseArticleSlice = (slice: ArticleSlice): Article => {
    const sliceValue = slice.value[0];
    const title = sliceValue.title;

    return {
      title,
      content: [<RichText key="0" render={sliceValue.content} />],
    };
  };

  static parseInterlinkingDocument = (
    document: Document,
  ): InterlinkingBlockProps => {
    const interDoc = document as unknown as InterlinkingDocument;
    const title = interDoc.data.title;
    const interlinkingDocumentContentLinks: InterlinkingBlockColumn[] = (
      interDoc.data.links || []
    ).map((linkData: Record<string, unknown>) => {
      const skippableIndices: number[] = [];
      return Object.keys(linkData).reduce<InterlinkingBlockColumn>(
        (result, link) => {
          const isUrl = link.startsWith('url');
          const isTitle = link.startsWith('title');

          if (isUrl || isTitle) {
            const textIndex = link.split(isUrl ? 'url' : 'title')[1];
            const index = parseInt(textIndex, 10);
            if (Number.isNaN(index)) {
              return result;
            }
            if (skippableIndices.includes(index)) {
              return result;
            }
            skippableIndices.push(index);
            const titleVal = linkData[`title${index}`];
            const urlData = linkData[`url${index}`];

            if (typeof titleVal !== 'string' || urlData == null) {
              return result;
            }

            let url: string | null = null;
            if (typeof urlData === 'string') {
              url = urlData;
            } else if (
              typeof urlData === 'object' &&
              urlData !== null &&
              'url' in (urlData as Record<string, unknown>) &&
              typeof (urlData as { url?: unknown }).url === 'string'
            ) {
              url = (urlData as { url: string }).url;
            }

            if (!url) {
              return result;
            }
            url = url.endsWith('/') ? url : `${url}/`;

            result.splice(index, 0, { url, title: titleVal });
          }
          return result;
        },
        [],
      );
    });
    return { title, linkColumns: interlinkingDocumentContentLinks };
  };
}

export default PrismicResponseParser;
